import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { TabHeader } from '../components/TabHeader';
import { Footer } from '../components/Footer';

/**
 * Lightweight placeholder used for routes whose real implementation is in
 * a later sprint: /investors (Sprint 3+), /:tab/ask (Sprint 4/5 answer
 * pages), /ask, and /legal/*. Keeps the header + footer chrome consistent
 * so the site never feels broken while content is being layered in.
 */
export function ComingSoonPage({
  title,
  sprint,
  body,
}: {
  title: string;
  sprint?: string;
  body?: string;
}): JSX.Element {
  const location = useLocation();
  const [params] = useSearchParams();
  const q = params.get('q');

  return (
    <div className="flex min-h-screen flex-col bg-sovereign-900">
      <TabHeader />
      <main className="flex-1 px-4 py-16 md:px-8 md:py-24">
        <section className="mx-auto max-w-2xl rounded-md border border-sovereign-line bg-white p-8 text-center shadow-card">
          <div className="mb-3 text-xs font-bold uppercase tracking-wider text-brand-purple">
            {sprint ?? 'Coming soon'}
          </div>
          <h1 className="mb-4 text-2xl font-bold text-ink md:text-3xl">{title}</h1>
          <p className="text-sm leading-relaxed text-ink-mute">
            {body ??
              `This page will populate in a later sprint of the DI Deck website rollout. In the meantime, use the Explore menu above to browse the tabs that are already live.`}
          </p>

          {q && (
            <div className="mt-6 rounded-md bg-sovereign-850 p-3 text-left text-xs text-ink-mute">
              <div className="font-semibold text-ink">Your question:</div>
              <div className="mt-1 italic">“{q}”</div>
              <div className="mt-3 text-[11px]">
                The AI search back-end (Cloudflare Worker at /api/ask) arrives in Sprint 5
                with RAG over the platform&apos;s public specification corpus. Until then,
                reach out via{' '}
                <a
                  href="mailto:sales@dideck.com"
                  className="text-brand-purple hover:underline"
                >
                  sales@dideck.com
                </a>
                .
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-center gap-2">
            <Link
              to="/"
              className="rounded-md border border-sovereign-line bg-white px-4 py-2 text-sm text-ink hover:border-brand-purple hover:text-brand-purple"
            >
              ← Back to home
            </Link>
            <a
              href="mailto:sales@dideck.com"
              className="rounded-md bg-brand-purple px-4 py-2 text-sm font-semibold text-white shadow-card hover:bg-brand-purple-dark"
            >
              Contact sales
            </a>
          </div>

          <div className="mt-4 text-[11px] text-ink-dim">Route: {location.pathname}</div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
